import { CreditsPlusIcon } from '@components/Icons/CreditsPlusIcon';
import LogOutIcon from '@components/Icons/LogOutIcon';
import { MobileMenuButton } from '@components/MobileMenuButton/MobileMenuButton';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { getAvatarInitial, getColorFromString } from '@lib/utils/colorUtils';
import { frostedGlassTransitionProps } from '@lib/utils/frostedPopoverTransition';
import { SECOND } from '@lib/utils/time';
import { formatCreditsWithUnit, formatDurationToString } from '@lib/utils/units';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Popover } from '@mui/material';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { formatDistance } from 'date-fns';
import { observer } from 'mobx-react';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import css from './AccountPage.module.scss';

export const AccountPage = observer(function AccountPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const user = userStore.data;
  const credits = billingStore.creditsLeft;

  const [creditsAnchor, setCreditsAnchor] = useState<HTMLElement | null>(null);
  const creditsOpen = Boolean(creditsAnchor);

  const openCredits = useCallback((e: MouseEvent<HTMLElement>) => {
    if (billingStore.isBillingDataStale(60 * SECOND)) {
      billingStore.forceRefreshBillingData();
    }
    setCreditsAnchor(e.currentTarget);
  }, []);
  const closeCredits = useCallback(() => setCreditsAnchor(null), []);

  useEffect(() => {
    if (billingStore.isBillingDataStale()) {
      billingStore.loadBillingFromLocalStorage();
    }
  }, []);

  if (!isMobile) {
    return <Navigate to="/settings" replace />;
  }

  if (!user) return null;

  const avatarColor = getColorFromString(user.email || '');
  const initials = getAvatarInitial(user);

  const ingestLeftFormatted = formatDurationToString(billingStore.ingestLeft ?? 0, {
    precision: 0,
  });
  const storageLeftFormatted = formatDurationToString(billingStore.storageLeft, {
    precision: 0,
    detail: 2,
  });

  return (
    <div className={css.accountPage}>
      <div className={css.profileSection}>
        <div className={css.avatar} style={{ background: avatarColor }}>
          {initials}
        </div>
        <span className={css.email}>{user.email}</span>
        {credits !== null && (
          <>
            <button
              className={classNames(css.creditsPill, { [css.creditsPillHidden]: creditsOpen })}
              onClick={openCredits}
            >
              <CreditsPlusIcon />
              <span>{formatCreditsWithUnit(credits, 'down')}</span>
            </button>
            <Popover
              open={creditsOpen}
              anchorEl={creditsAnchor}
              onClose={closeCredits}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              slotProps={{
                transition: frostedGlassTransitionProps,
                paper: {
                  sx: {
                    background: 'transparent !important',
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important',
                    overflow: 'visible !important',
                    borderRadius: '0 !important',
                  },
                },
              }}
            >
              <div className={css.creditsDropdown}>
                <div className={css.creditsTopRow}>
                  <span className={css.creditsBadge}>
                    {billingStore.data?.subscription?.product?.name || 'No plan'}
                  </span>
                  <div className={css.creditsTotalRow}>
                    <CreditsPlusIcon />
                    <span>{formatCreditsWithUnit(credits, 'down')}</span>
                  </div>
                </div>
                <div className={css.creditsSection}>
                  <div className={css.creditsRow}>
                    <span className={css.creditsLabel}>Media processing</span>
                    <span className={css.creditsValue}>≈ {ingestLeftFormatted} left</span>
                  </div>
                  <div className={css.creditsRow}>
                    <span className={css.creditsLabel}>Storage</span>
                    <span className={css.creditsValue}>= {storageLeftFormatted} left</span>
                  </div>
                  <div className={classNames(css.creditsRow, css.creditsLastUpdated)}>
                    <span className={css.creditsLabel}>Last updated</span>
                    <span className={css.creditsValue}>
                      {billingStore.lastUpdate
                        ? formatDistance(billingStore.lastUpdate, new Date(), { addSuffix: true })
                        : 'Never'}
                    </span>
                  </div>
                </div>
                <div className={css.creditsLinks}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      closeCredits();
                      navigate('/settings/billing');
                    }}
                    className={css.creditsLink}
                  >
                    Billing & Credits History ↗
                  </a>
                </div>
                <div className={css.creditsBottom}>
                  <span className={css.creditsMuted}>Need more?</span>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      closeCredits();
                      navigate('/pricing');
                    }}
                    className={css.creditsUpgrade}
                  >
                    Upgrade plan ↗
                  </a>
                </div>
              </div>
            </Popover>
          </>
        )}
      </div>

      <div className={css.menuList}>
        <MobileMenuButton
          icon={<SettingsRoundedIcon />}
          label="Settings"
          onClick={() => {
            trackingStore.navItemClicked('Settings', '/settings');
            navigate('/settings');
          }}
        />
        <MobileMenuButton
          icon={<HelpOutlineRoundedIcon />}
          label="Help center"
          onClick={() => {
            trackingStore.navItemClicked('Help center', 'intercom');
            try {
              showIntercom();
            } catch (e) {
              console.warn('Intercom not ready:', e);
              window.open('mailto:support@starzero.ai', '_blank');
            }
          }}
        />
        <MobileMenuButton
          icon={<ShieldOutlinedIcon />}
          label="Privacy policy"
          onClick={() => {
            trackingStore.navItemClicked('Privacy policy', 'https://starzero.ai/privacy/');
            window.open('https://starzero.ai/privacy/', '_blank');
          }}
        />
        <MobileMenuButton
          icon={<ArticleOutlinedIcon />}
          label="Terms and conditions"
          onClick={() => {
            trackingStore.navItemClicked('Terms and conditions', 'https://starzero.ai/terms/');
            window.open('https://starzero.ai/terms/', '_blank');
          }}
        />
        <MobileMenuButton
          icon={<LogOutIcon />}
          label="Log out"
          onClick={() => {
            trackingStore.navItemClicked('Log out', 'logout');
            userStore.logout();
          }}
        />
      </div>
    </div>
  );
});
